/**
 * World building sheets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const worldBuildingSheetsPack: ToilePack = {
    slug: 'world-building-sheets',
    category: 'tabletop',
    name: 'World building sheets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'kingdom', label: 'Kingdom sheet', subject: 'a sheet titled "Kingdom" with labelled ruled sections "Ruler", "Capital", "Allies" and "Enemies", and an empty shield outline for the heraldry'},
        {key: 'city', label: 'City sheet', subject: 'a sheet titled "City" with a large empty square map box in the upper half and labelled ruled sections "Districts", "Factions" and "Landmarks" below'},
        {key: 'pantheon', label: 'Pantheon sheet', subject: 'a sheet titled "Pantheon" with six ruled boxes in a grid, each with an empty symbol circle and lines for "Name" and "Domain"'},
    ],
    styles: [
        {key: 'atlas', label: 'Atlas', style: 'an old atlas page look with a compass motif, inked headings and fine ruled sections', palette: 'sepia ink on aged cream paper', framing: 'the page filled inside a wide margin'},
        {key: 'heraldic', label: 'Heraldic', style: 'a heraldic look with a bordered banner header and shield motifs in the corners', palette: 'deep red and gold on cream', framing: 'the page filled inside a wide margin'},
        {key: 'plain', label: 'Plain worksheet', style: 'a plain worksheet with thin ruled boxes, bold headings and empty map space', palette: 'black ink only', framing: 'the page filled inside a wide margin'},
    ],
    directions: ['space empire', 'desert realm', 'island nation'],
    directionHint: 'try: space empire',
    seo: {
        title: 'World building sheets: kingdom, city, pantheon',
        metaDescription: 'Printable world building sheets in three styles: a kingdom sheet with a shield, a city sheet with a map box and a pantheon of gods.',
        h1: 'World building sheets: kingdom sheet, city sheet, pantheon sheet',
        tagline: 'Nine sheets: a kingdom, a city and a pantheon, in three styles.',
        intro: 'Writers and game masters fill in these sheets to build a setting piece by piece: a kingdom, a city or a pantheon. This pack shows a kingdom sheet with an empty heraldry shield, a city sheet with a large map box, and a pantheon sheet with six boxes for gods. Each comes as an old atlas page, a heraldic banner or a plain worksheet. Type a setting, such as a space empire, and the sheet keeps its open boxes.',
        useCases: [
            'a kingdom for a campaign setting',
            'a city map box for a town the party visits',
            'a pantheon of gods for a game world',
            'a worksheet for the setting of a story',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three readings in three styles: a kingdom sheet, a city sheet with a map box, and a pantheon sheet of six gods.'},
            {q: 'Is the map box empty?', a: 'Yes. The city sheet has a large empty square map box in the upper half, for drawing the city.'},
            {q: 'What does the heraldic style add?', a: 'A bordered banner header, shield motifs in the corners, and deep red and gold on cream.'},
            {q: 'What is the plain worksheet?', a: 'Thin ruled boxes, bold headings and empty map space, in black ink only.'},
        ],
    },
}
